import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "484",
  "slug": "mesa-vs-butte-vs-plateau-flat-topped-landforms",
  "title": "Mesa vs. Butte vs. Plateau: How Flat-Topped Landforms Form and Differ",
  "excerpt": "Mesas, buttes and plateaus are all flat on top and steep on the sides, but they are not the same thing. Here is how a hard cap of rock builds them, and the simple rule that tells them apart.",
  "date": "2026-06-19",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1695838533256-6a2b10a5afb1?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxtZXNhJTIwYnV0dGUlMjBkZXNlcnR8ZW58MXwwfHx8MTc4MTg1Njc4N3ww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A plateau, a mesa, and a butte are all flat-topped landforms protected by a hard layer of cap rock — they differ mainly in size. Plateaus are large elevated tablelands, mesas are smaller flat-topped hills wider than they are tall, and buttes are narrow towers taller than they are wide, often the eroded remnant of a mesa.",
  "content": [
    {
      "type": "paragraph",
      "text": "The deserts of the American Southwest, parts of Spain, southern Africa and Australia share a distinctive skyline: flat tablelands and lonely towers rising sharply from the plain, as if the land had been sliced with a knife. These are plateaus, mesas and buttes. They look dramatic, but they all come from the same simple idea — a hard layer of rock protecting softer rock underneath."
    },
    {
      "type": "heading",
      "text": "One Process, Three Sizes"
    },
    {
      "type": "paragraph",
      "text": "Flat-topped landforms form through differential erosion: when layers of rock erode at different speeds. A region of flat-lying sedimentary rock often has a tough, resistant layer — a caprock — sitting on top of softer material. Where that caprock survives, it shields the rock beneath it from rain, rivers and wind. Where the caprock has been breached, erosion bites quickly into the soft layers and carves away the land around it. What is left standing are flat-topped blocks capped by the resistant rock, with steep sides where the soft rock has been stripped back."
    },
    {
      "type": "paragraph",
      "text": "Over enormous spans of time, a single landscape can pass through all three forms. A broad plateau gets dissected by canyons and retreating cliffs into smaller mesas. Mesas keep shrinking as their edges crumble until only a narrow butte remains. Eventually even the butte erodes down to a spire or disappears entirely."
    },
    {
      "type": "heading",
      "text": "What Is a Plateau?"
    },
    {
      "type": "paragraph",
      "text": "A plateau is a large area of relatively flat, elevated land that rises sharply above the surrounding country on at least one side. Plateaus can stretch for hundreds of kilometres — think of the Colorado Plateau, the Deccan Plateau of India, or the vast Tibetan Plateau. They are the starting point: the big, raised tablelands that smaller flat-topped features are eventually carved out of."
    },
    {
      "type": "heading",
      "text": "What Is a Mesa?"
    },
    {
      "type": "paragraph",
      "text": "Mesa is the Spanish word for table, and that is exactly what it looks like: a flat-topped hill or small mountain with steep sides, broader than it is tall. A mesa is essentially an isolated remnant of a plateau, left behind after erosion has eaten away the land around it but before the caprock has been fully stripped off. The flat top marks the level of the old, more extensive surface."
    },
    {
      "type": "heading",
      "text": "What Is a Butte?"
    },
    {
      "type": "paragraph",
      "text": "A butte is the next stage down: a tall, narrow tower of rock, taller than it is wide, with a small flat or rounded top. Buttes are usually the worn-down remains of mesas, reduced to a slender column as their edges keep retreating. The dramatic standing towers of Monument Valley are classic buttes. The loose rule of thumb that geographers use is straightforward:"
    },
    {
      "type": "list",
      "items": [
        "Plateau: a broad, elevated tableland, often vast in extent.",
        "Mesa: flat-topped and steep-sided, wider than it is tall — an isolated piece of former plateau.",
        "Butte: flat- or round-topped and steep-sided, taller than it is wide — a worn-down remnant of a mesa.",
        "All three are protected by a resistant caprock; remove the cap, and erosion quickly levels what is left."
      ]
    },
    {
      "type": "heading",
      "text": "Why It Helps to Know the Difference"
    },
    {
      "type": "paragraph",
      "text": "Flat-topped landforms are a strong regional clue. Extensive mesa-and-butte country, red rock and dry scrub point you toward arid plateau regions like the American Southwest, while the same shapes in other colours and climates can signal places like the Spanish interior or the South African Karoo. Learning to spot a mesa from a butte will not just settle an argument on a road trip — it is the kind of detail that quietly narrows down where on Earth you are, whether you are reading a real map or chasing a location in EarthGuessr."
    }
  ]
};
